// Admin → Error Log
//
// Shows server-captured errors (from sendServerError + the unhandled
// Express error middleware). Each row has the message, source route,
// stack trace, request body preview, and the user identity from the
// audit headers. Open errors are listed first by default; admins can
// toggle to see resolved or all.
//
// Resolving an error keeps the row for history but stamps resolvedAt /
// resolvedBy / resolutionNote so it stops cluttering the open queue.

import React, { useEffect, useState, useCallback } from 'react';
import { auditFetch } from '../auditFetch';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

export default function AdminErrorLog() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('open'); // open | resolved | all
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [resolveNote, setResolveNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await auditFetch(`${API_BASE}/error-log?status=${status}&limit=200`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.message || `HTTP ${res.status}`);
      setRows(Array.isArray(data.errors) ? data.errors : []);
      setTotal(data.total || 0);
    } catch (e) { setError(e.message || 'Failed to load errors'); }
    setLoading(false);
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    try {
      const res = await auditFetch(`${API_BASE}/error-log/${id}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: resolveNote || '' }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setResolveNote(''); setExpandedId(null);
      await load();
    } catch (e) { setError(e.message); }
  };

  // One-click bulk-resolve. Confirms first so an admin can't nuke the
  // current backlog by accident.
  const resolveAll = async () => {
    if (!window.confirm('Mark every currently-unresolved error as resolved? This cannot be undone in bulk (you can still re-open rows individually).')) return;
    try {
      const res = await auditFetch(`${API_BASE}/error-log/resolve-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: 'bulk resolve' }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json().catch(() => ({}));
      setExpandedId(null);
      await load();
      if (typeof data.updated === 'number') {
        setError(''); // clear any previous error
        // The load() resets the table; provide a transient confirmation via console + alert.
        window.alert(`Resolved ${data.updated} unresolved row(s).`);
      }
    } catch (e) { setError(e.message); }
  };

  const reopen = async (id) => {
    try {
      const res = await auditFetch(`${API_BASE}/error-log/${id}/reopen`, { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) { setError(e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm('Permanently delete this error row? Audit history is preserved separately.')) return;
    try {
      const res = await auditFetch(`${API_BASE}/error-log/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) { setError(e.message); }
  };

  const formatTs = (ts) => {
    if (!ts) return '—';
    try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
  };

  return (
    <div className="admin-section">
      <h2 style={{ marginTop: 0 }}>Error Log</h2>
      <p style={{ color: '#555', fontSize: '0.9rem', marginBottom: '1rem' }}>
        Every server error captured automatically — both ones that came from a route handler's
        explicit error response and ones that escaped to the global error middleware. Click a row
        to expand the stack, request body, and user context. Mark <strong>Resolved</strong> once
        you've fixed the underlying cause; resolved rows stay for history.
      </p>

      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <label style={{ fontWeight: 600 }}>Show:</label>
        {['open', 'resolved', 'all'].map(s => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            style={{
              padding: '0.4rem 0.85rem',
              background: status === s ? '#1565c0' : 'white',
              color: status === s ? 'white' : '#1565c0',
              border: '1px solid #1565c0',
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.85rem',
            }}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <button onClick={load} style={{ padding: '0.4rem 0.85rem', background: 'white', border: '1px solid #999', borderRadius: 4, cursor: 'pointer', fontSize: '0.85rem' }}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        <button onClick={resolveAll} title="Mark every currently-unresolved row as resolved" style={{ padding: '0.4rem 0.85rem', background: '#2e7d32', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
          Resolve all unresolved
        </button>
        <span style={{ color: '#000', fontSize: '0.85rem', fontWeight: 600 }}>
          {total} {status} error{total === 1 ? '' : 's'}
        </span>
      </div>

      {error && <div style={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', padding: '0.5rem 0.75rem', borderRadius: 4, marginBottom: '0.75rem' }}>{error}</div>}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#fafafa', borderBottom: '2px solid #e0e0e0' }}>
            <th style={th}>Captured</th>
            <th style={th}>Source</th>
            <th style={th}>Message</th>
            <th style={th}>User</th>
            <th style={th}>Status</th>
            <th style={th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && !loading && (
            <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: '#888', padding: '2rem' }}>No {status} errors. 🎉</td></tr>
          )}
          {rows.map(r => (
            <React.Fragment key={r.id}>
              <tr
                style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer', background: expandedId === r.id ? '#fff8e1' : 'transparent' }}
                onClick={() => { setExpandedId(expandedId === r.id ? null : r.id); setResolveNote(''); }}
              >
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{formatTs(r.capturedAt)}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.78rem', maxWidth: 280, wordBreak: 'break-word' }}>{r.source || `${r.requestMethod || ''} ${r.requestPath || ''}`}</td>
                <td style={{ ...td, color: '#c62828', maxWidth: 480, wordBreak: 'break-word' }}>{r.message}</td>
                <td style={{ ...td, fontSize: '0.78rem' }}>{r.userEmail || r.userName || '—'}</td>
                <td style={{ ...td, fontSize: '0.78rem' }}>
                  {r.resolvedAt
                    ? <span style={{ color: '#1b5e20' }}>resolved {formatTs(r.resolvedAt).split(',')[0]}</span>
                    : <span style={{ color: '#c62828', fontWeight: 600 }}>open</span>}
                </td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {r.resolvedAt
                    ? <button onClick={(e) => { e.stopPropagation(); reopen(r.id); }} style={btnSmall}>Reopen</button>
                    : <button onClick={(e) => { e.stopPropagation(); setExpandedId(r.id); }} style={btnSmall}>Resolve…</button>}
                  <button onClick={(e) => { e.stopPropagation(); remove(r.id); }} style={{ ...btnSmall, color: '#c62828', marginLeft: 4 }}>Delete</button>
                </td>
              </tr>
              {expandedId === r.id && (
                <tr style={{ background: '#fff8e1' }}>
                  <td colSpan={6} style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '0.4rem 1rem', fontSize: '0.8rem' }}>
                      <strong>Request:</strong>
                      <code>{r.requestMethod || '?'} {r.requestPath || '(no path)'}</code>

                      <strong>Status code:</strong>
                      <code>{r.statusCode ?? '?'}</code>

                      <strong>User:</strong>
                      <code>{r.userEmail || '?'} {r.userName ? `(${r.userName})` : ''}</code>

                      {r.requestBodyPreview && (
                        <>
                          <strong>Request body:</strong>
                          <pre style={preStyle}>{r.requestBodyPreview}</pre>
                        </>
                      )}

                      {r.contextJson && (
                        <>
                          <strong>Context:</strong>
                          <pre style={preStyle}>{r.contextJson}</pre>
                        </>
                      )}

                      {r.stack && (
                        <>
                          <strong>Stack:</strong>
                          <pre style={preStyle}>{r.stack}</pre>
                        </>
                      )}

                      {r.resolvedAt && (
                        <>
                          <strong>Resolved:</strong>
                          <span>{formatTs(r.resolvedAt)} by {r.resolvedBy || '?'}</span>
                          {r.resolutionNote && (<>
                            <strong>Resolution note:</strong>
                            <span>{r.resolutionNote}</span>
                          </>)}
                        </>
                      )}
                    </div>

                    {!r.resolvedAt && (
                      <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                        <textarea
                          value={resolveNote}
                          onChange={e => setResolveNote(e.target.value)}
                          placeholder="Resolution note (optional) — what was the cause + fix?"
                          rows={2}
                          style={{ flex: 1, padding: '0.4rem', border: '1px solid #ccc', borderRadius: 4, fontFamily: 'inherit', fontSize: '0.85rem' }}
                        />
                        <button onClick={() => resolve(r.id)} style={{ padding: '0.5rem 1rem', background: '#1b5e20', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
                          Mark resolved
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const th = { textAlign: 'left', padding: '8px 10px', fontWeight: 700, color: '#333' };
const td = { padding: '8px 10px', verticalAlign: 'top' };
const btnSmall = { padding: '3px 10px', fontSize: '0.78rem', background: 'white', border: '1px solid #ccc', borderRadius: 3, cursor: 'pointer' };
const preStyle = { margin: 0, padding: '0.5rem', background: 'white', border: '1px solid #ddd', borderRadius: 3, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 300, overflowY: 'auto' };
